/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useEffect, useRef } from 'react';
import { Link, useParams, useNavigate } from "react-router-dom";
import { CommonImageInput, CommonInput, PageToolbar, CustomFileSelector } from "../common";
import SimpleEditor from "../common/SimpleEditor";
import helpers from "../../_helpers/common";
import { Category, Blog } from "../../types/interfaces"
import { createFormErrors, FormError } from '../../utility/formUtils';
import useCommonActions from "../../hooks/useCommonActions";
import useAxios from "../../hooks/useAxios";

type BlogErrors = Record<string, string>;

const CreateUpdateBlog: React.FC = () => {
  const axiosInstance  = useAxios();
  const { blog_id }    = useParams();
  const navigate       = useNavigate();
  const [ categories, setCategory ] = useState<Category[] | null>(null);
  const [ formData, setFormData ] = useState<Blog>({ category_id: '', title: '', excerpt: '', description: '', tags: '', 
                                                        display_image: '', banner_image: '', status: 1 });
  const [ errors, setErrors ] = useState<Partial<Blog>>({});
  const [ displayimage, setDisplayImage ] = useState<File | null>(null);
  const [ bannerimage, setBannerImage ] = useState<File | null>(null);
  const [ formSubmitted, setFormSubmitted ] = useState(false);
  const [ isSubmitting, setIsSubmitting ] = useState(false);
  const descriptionRef = useRef<any>(null);
 
  useEffect(() => {
	//if(blog_id !== '0'){  
	  getFormData();
	//}
  }, [])
  
  //reset banner and display image error on set
  useEffect(() => {
	if(displayimage){
		setErrors((prev) => ({
			...prev,
			display_image: undefined,
		}));
	}
	if(bannerimage){
		setErrors((prev) => ({
			...prev,
			banner_image: undefined,
		}));
	}
  }, [bannerimage, displayimage])
  
  const getFormData = async() => {
	try {
	  const response: any = await axiosInstance({
		url: `blogs/get-form-data`,
		method: "GET",
		params: {blogId: blog_id},
	  });
	  console.log('Form Data response', response);
	  if(!response.error){
		setCategory(response.data.categories);  
		if(blog_id !== '0'){
			let blog = response.data.blog;
			setFormData({ ...formData,
			  category_id: blog.category_id, title: blog.title, excerpt: blog.excerpt, description: blog.description, 
			  tags: blog.tags, seo_title: blog.seo_title, seo_keyword: blog.seo_keyword, 
			  seo_description: blog.seo_description, display_image: blog.displaypath, 
			  banner_image: blog.bannerpath, status: blog.status
			})
		}
	  }
	} catch (error: any) {
	  console.log('catch error', error)
	} finally {
	  
	}
  }

  // Handle form input changes
  const handleFormInputChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement |HTMLTextAreaElement>
  ) => {
    const { name, value } = event.target;
    
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
	//reset the error
	setErrors((prev) => ({
		...prev,
		[name]: undefined,
	}));
  };
  
  const validate = async (): Promise<boolean> => {
	  // Initialize empty error object using the utility
	  //const newErrors = createFormErrors(formData);
      const newErrors: Partial<Blog> = {};
	  // Validate fields
	   if (!formData.category_id) {
		newErrors.category_id = 'Category is required';
	  }
	  if (!formData.title) {
		newErrors.title = 'Title is required';
	  }
	  
	  if (!formData.description) {
		newErrors.description = 'Description is required';
	  }

	  if (!formData.excerpt) {
		newErrors.excerpt = 'Excerpt is required';
	  }

	  if (!formData.status) {
		newErrors.status = 'Status is required';
	  }

	  if (!displayimage && formData.display_image === '') {
	    newErrors.display_image = "Blog display image is required";
	  }
	  
	  if (!bannerimage && formData.banner_image === '') {
	    newErrors.display_image = "Blog banner image is required";
	  }
      //console.log('newErrors', newErrors);
	  setErrors(newErrors);

	  // Return true if there are no errors
	  return Object.keys(newErrors).length === 0;
  };

  /*Submit Form*/
  const submitForm = async (e: React.FormEvent) => {
    e.preventDefault();
	let isValid = await validate();
	
	if (isValid) {
	  setIsSubmitting(true);
	  var form_data = await prepareSubmitData();
	  try {
		const response: any = await axiosInstance({
			url: 'blogs/create-update',
			method: "POST",
			data: form_data,
			headers: {
			  'Content-Type': 'multipart/form-data',
			},
			timeout: 40000,
		});
		
		//on product creation success redirect back to dashbaord
		if(!response.error){
		  navigate('/blog-list');
		}
	  } catch (error: any) {
		//console.log("validation_error catch", error?.response);

		  if (error?.response?.data?.type === "validation_error") {
			const errorsObj = error.response.data.message.errors as Record<string, string[]>;

			const newErrors: BlogErrors = {};

			(Object.keys(errorsObj) as Array<keyof Blog>).forEach((field) => {
			  newErrors[field] = errorsObj[field][0];
			});

			setErrors(newErrors);
		  
		  } else {
			console.log("catch error", error);
		  }
	  } finally {
		setIsSubmitting(false);
	  }
	} 
  }
  
  /*Preapare Submit Form Data*/
  const prepareSubmitData = async () => {
	const form_Data = new FormData();
	
	if(displayimage){
	  form_Data.append('display_image', displayimage);
    }
	
	if(bannerimage){
	  form_Data.append('banner_image', bannerimage);
    }
	if(blog_id !== '0'){
		form_Data.append('_id', blog_id);
	}
	form_Data.append(
	  'category_id',
	  typeof formData.category_id === 'string'
		? formData.category_id
		: formData.category_id?._id?.toString() || ''
	);
	form_Data.append('title', formData.title);
	form_Data.append('excerpt', formData.excerpt);
	form_Data.append('description', formData.description);
	form_Data.append('tags', formData.tags);
	form_Data.append('status', (formData.status ?? 0).toString());
	form_Data.append('seo_title', formData.seo_title || '');
	form_Data.append('seo_keyword', formData.seo_keyword || '');
	form_Data.append('seo_description', formData.seo_description || '');
	return form_Data;
  }
 
  return (
    <div className="d-flex flex-column flex-column-fluid">
      {/*Page toolbar Area Start*/}
	  <PageToolbar title={blog_id === '0' ? "Create Blog" : "Update Blog"} />
      {/*Page toolbar Area End*/}
      {/*Page Conent Area Start*/}
      <div className="app-content flex-column-fluid">
        <div className="app-container">
          <form className="form d-flex flex-column flex-lg-row">
            <div className="d-flex flex-column gap-7 gap-lg-10 w-100 w-lg-300px mb-3 me-lg-10">
              <div className="card card-flush py-4">
                <div className="card-body pt-0">
                  <label className="required form-label">Display IMage</label>
                  <br />
                  <div className="text-center mb-2">
                    <CommonImageInput
                      fieldName="display_image"
                      defaultImage={formData.display_image !== '' ? formData.display_image : "/assets/media/svg/blank-image.svg"}
                      onImageChange={(file) => setDisplayImage(file)}
                    />
                  </div>
				  {errors.display_image && (
					  <span className="fv-plugins-message-container fv-plugins-message-container--enabled invalid-feedback">
						{errors.display_image}
					  </span>
				  )}
                  <div className="text-muted fs-7">
                    Set the blog display image. Only *.png, *.jpg and *.jpeg
                    image files are accepted
                  </div>
                </div>
              </div>
			  
			  <div className="card card-flush py-4">
                <div className="card-body pt-0">
                  <label className="required form-label">Banner Image</label>
                  <br />
                  <div className="text-center mb-2">
                    <CommonImageInput
                      fieldName="display_image"
                      defaultImage={formData.banner_image !== '' ? formData.banner_image : "/assets/media/svg/blank-image.svg"}
                      onImageChange={(file) => setBannerImage(file)}
                    />
                  </div>
				  {errors.banner_image && (
					  <span className="fv-plugins-message-container fv-plugins-message-container--enabled invalid-feedback">
						{errors.banner_image}
					  </span>
				  )}
                  <div className="text-muted fs-7">
                    Set the blog banner image. Only *.png, *.jpg and *.jpeg
                    image files are accepted
                  </div>
                </div>
              </div>
            </div>
            <div className="d-flex flex-column flex-row-fluid gap-7 gap-lg-8">
              <div className="card card-flush py-3">
                <div className="card-body pt-0">
				   <div className="mb-4 fv-row fv-plugins-icon-container">
					  <label className="required form-label">Category</label>
					  <select
						className={`form-select mb-2 ${errors.category_id ? "is-invalid" : ""}`}
						name="category_id"
						value={typeof formData.category_id === 'string' ? formData.category_id : ''}
						onChange={handleFormInputChange}
					  >
						<option value="">Select an Category</option>
						{categories?.map((cat) => (
						  <option key={cat._id} value={cat._id}>{cat.title}</option>
						))}
					  </select>
					  {errors.category_id && (
						  <div className="fv-plugins-message-container fv-plugins-message-container--enabled invalid-feedback">
							{typeof errors.category_id === "string" ? errors.category_id : ''}
						  </div>
					  )}
					  <div className="text-muted text-info fs-7">
						Set the product category.
					  </div>
				  </div>
                  <div className="mb-4 fv-row fv-plugins-icon-container">
                    <label className="required form-label">Blog Title</label>
                    <CommonInput
                      type="text"
                      name="title"
                      placeholder="Blog title"
                      value={formData.title}
                      onChange={handleFormInputChange}
					  error={errors.title}
                    />
                  </div>
				  <div className="mb-4 fv-row fv-plugins-icon-container">
                    <label className="required form-label">Blog Excerpt</label>
                    <CommonInput
                      type="text"
                      name="excerpt"
                      placeholder="Blog excerpt"
                      value={formData.excerpt}
                      onChange={handleFormInputChange}
					  error={errors.excerpt}
                    />
                  </div>
                  <div className="mb-4">
					<SimpleEditor
					  label="Description"
					  required={true}
					  value={formData.description || ""}
					  onChange={(content) => {
						setFormData((prev) => ({ ...prev, description: content }));

						// reset error
						setErrors((prev) => ({ ...prev, description: undefined }));
					  }}
					  placeholder="Enter description..."
					  height={100}
					  error={errors?.description}
					/>
                    <div className="text-muted fs-7">
                      Set a description to the blog.
                    </div>
                  </div>
				  <div className="fv-row">
					<div className="row gap-2">
				      {/*Price Section Start*/}
				      <div className="col-md-12">
					     <div className="mb-4 fv-row fv-plugins-icon-container">
							<label className="required form-label">Status</label>
							  <select
								className={`form-select mb-2 ${errors.status ? "is-invalid" : ""}`}
								name="status"
								value={formData.status}
								onChange={handleFormInputChange}
							  >
								<option value="">Select an Status</option>
								<option value="1">Active</option>
								<option value="0">Inactive</option>
								{/*<option value="scheduled">Completed</option>
									  <option value="unpublished">Cancelled</option>*/}
							  </select>
							  {errors.status && <div className="fv-plugins-message-container fv-plugins-message-container--enabled invalid-feedback">{errors.status}</div>}
							  
						 </div>
					  </div>
					  <div className="col-mb-12">
					     <div className="mb-4 fv-row fv-plugins-icon-container">
							<label className="required form-label">Tags</label>
							  <CommonInput
								  type="text"
								  name="tags"
								  placeholder="Enter Product tags"
								  value={formData.tags}
								  onChange={handleFormInputChange}
								  error={errors.tags}
								/>
							  <div className="text-muted text-info fs-7">
								Set the blogs tags comma seprated.
							  </div>
						 </div>
					  </div>
					</div>  
				  </div>
                  </div>
                </div>
				<div className="fv-row mb-10">
				  <div className="row g-9">
					<div className="col">
					  <div className="card card-flush py-1">

						{/* Header */}
						<div className="card-header d-flex justify-content-between align-items-center">
						  <div className="card-title">
							<h2 className="mb-0">SEO Details</h2>
						  </div>

						  {/* Collapse Button */}
						  <button
							className="btn btn-sm btn-light-primary"
							type="button"
							data-bs-toggle="collapse"
							data-bs-target="#seoDetailsCollapse"
							aria-expanded="true"
							aria-controls="seoDetailsCollapse"
						  >
							Toggle
						  </button>
						</div>

						{/* Body (Collapsable) */}
						<div id="seoDetailsCollapse" className="collapse show">
						  <div className="card-body pt-0">
							<div className="mb-10">
							  <label className="form-label">Meta Tag Title</label>
							  <CommonInput
								type="text"
								name="seo_title"
								placeholder="Meta tag name"
								value={formData.seo_title}
								onChange={handleFormInputChange}
							  />
							</div>

							<div className="mb-10">
							  <label className="form-label">Meta Tag Description</label>
							  <CommonInput
								type="text"
								name="seo_description"
								placeholder="Meta Tag Description"
								value={formData.seo_description}
								onChange={handleFormInputChange}
							  />
							</div>

							<div className="mb-15">
							  <label className="form-label">Meta Tag Keywords</label>
							  <CommonInput
								type="text"
								name="seo_keyword"
								placeholder="Meta Tag Keywords"
								value={formData.seo_keyword}
								onChange={handleFormInputChange}
							  />

							  <div className="text-muted fs-7">
								Set a list of keywords that the product is related to. Separate the keywords by adding a comma{" "}
								<code>,</code> between each keyword.
							  </div>
							</div>
						  </div>
						</div>

					  </div>
					</div>
				  </div>
				</div>
              <div className="d-flex justify-content-end">
                <Link to="/product-list" className="btn btn-light me-5">
                  Cancel
                </Link>
				{isSubmitting ? (
				  <button
					type="button"
					className="btn btn-primary"
					disabled={true}
				  >
					<span
					  className="indicator-progress"
					  style={{ display: "block" }}
					>
					  Please wait...
					  <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
					</span>
				  </button>
				) : (
				  <button
					type="button"
					className="btn btn-primary"
					onClick={(e) => submitForm(e)}
				  >
					<span className="indicator-label">Submit</span>
				  </button>
				)}
              </div>
            </div>
          </form>
        </div>
      </div>
      {/*Page Conent Area End*/}
    </div>
  );
};

export default CreateUpdateBlog;
